import { ComponentType, useMemo, useState } from 'react';
import { Styles } from 'react-select';

import { createForm, useCreateFormStore } from '@components/form';
import { FormLabel } from '@components/form-label';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { IconProps } from '@components/icons/base';
import { IconDynamicField } from '@components/icons/dynamic-field';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { InfoBox } from '@components/info-box';
import { List } from '@components/integrations/integrations-types';
import { Separator } from '@components/separator';
import { Sidebar, forceHideSidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useEditDocumentTemplate } from 'api/documents/hooks';
import {
  DocumentTemplate,
  DocumentTemplateField,
  DocumentTemplateFieldTypes,
} from 'api/documents/requests';

import {
  Container,
  DATE_FORMAT_OPTIONS,
  DATE_LANGUAGE_OPTIONS,
  DECIMAL_PLACES,
  DecimalFormatterType,
  NumberExampleCard,
  NumberFormatterType,
  TYPE_ICON_MAP,
  useDecimalFormatOptions,
  useNumberFormatOptions,
} from './common';

type FormData = Pick<DocumentTemplateField, 'label' | 'type'> & {
  defaultValue: string;
  optionsFrom?: string;
  dateFormat?: string;
  numberFormat?: string;
  decimalPlaces?: number;
  locale?: string;
};
type IconType = ComponentType<IconProps>;
const Form = createForm<FormData>();

export const useEditFieldSidebar = () => useSidebar(EditFieldSidebar, { variant: 'CONTENT_HEAVY' });

const EditFieldSidebar = ({
  document,
  field,
  lists = [],
}: {
  document: DocumentTemplate;
  field: DocumentTemplateField;
  lists: Array<List>;
}) => {
  const { t } = useScopedTranslate(locale);
  const editTemplate = useEditDocumentTemplate();
  const defaultValue = document.defaultData?.[field.id];
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const fieldType = formStore.useFieldValue('type');
  const optionsFrom = formStore.useFieldValue('optionsFrom');
  const selectedList = lists.find((list) => list.id === optionsFrom);
  const fieldIndex = document.fieldConfig.findIndex(({ id }) => id === field.id);

  const [allowDecimalNumbers, setAllowDecimalNumbers] = useState(field.type === 'decimal');
  const formDecimalPlaces = formStore.useFieldValue('decimalPlaces') as number;
  const formNumberFormat = formStore.useFieldValue('numberFormat') as
    | NumberFormatterType
    | DecimalFormatterType;
  const numberFormatOptions = useNumberFormatOptions();
  const decimalFormatOptions = useDecimalFormatOptions(2);
  const switchChanged = allowDecimalNumbers !== (field.type === 'decimal');
  const isSubmitDisabled = !isDirty && !switchChanged;

  const currentLocale =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';

  const typeOptions: Array<[DocumentTemplateFieldTypes, IconType]> = useMemo(() => {
    return Object.entries(TYPE_ICON_MAP)
      .filter(([type]) => type !== 'decimal')
      .map(([type, currentIcon]) => {
        const newIcon =
          type === 'number' && allowDecimalNumbers
            ? (TYPE_ICON_MAP.decimal as IconType)
            : (currentIcon as IconType);

        return [type as DocumentTemplateFieldTypes, newIcon];
      });
  }, [allowDecimalNumbers]);

  const handleSubmit = async ({ defaultValue, ...fieldData }: FormData) => {
    const fieldConfig = [...document.fieldConfig];
    fieldConfig[fieldIndex] = {
      ...fieldConfig[fieldIndex],
      ...fieldData,
      ...(allowDecimalNumbers && { type: 'decimal' }),
    };
    const defaultData = { ...document.defaultData };
    defaultData[field.id] = defaultValue;
    await editTemplate.mutateAsync({
      fieldConfig,
      defaultData,
      id: document.shortId,
    });
    forceHideSidebar();
  };

  const getExtraFields = (fieldType: DocumentTemplateFieldTypes) => {
    if (fieldType === 'select') {
      return (
        <Form.Select
          id="optionsFrom"
          label={t('list')}
          options={
            lists?.map((list) => ({
              value: list.id,
              label: list.name,
            })) ?? []
          }
          isClearable={false}
          defaultValue={field.optionsFrom}
        />
      );
    }
    if (fieldType === 'date') {
      return (
        <>
          <Form.Select
            id="dateFormat"
            label={t('format')}
            defaultValue={field.dateFormat}
            options={DATE_FORMAT_OPTIONS}
            isClearable={false}
            optionContentFullWidth
            placeholder={t('addValuePlaceholder')}
          />
          <Form.Select
            id="locale"
            label={t('language')}
            options={DATE_LANGUAGE_OPTIONS}
            isClearable={false}
            defaultValue={
              field.locale ??
              DATE_LANGUAGE_OPTIONS.find((option) => option.value.includes(currentLocale))?.value
            }
          />
        </>
      );
    }
    if (['number', 'decimal'].includes(fieldType)) {
      return (
        <>
          <Switch
            label={t('allowDecimalNumbers')}
            defaultValue={allowDecimalNumbers}
            onChange={setAllowDecimalNumbers}
          />
          {allowDecimalNumbers ? (
            <>
              <Form.Select
                id="decimalPlaces"
                label={t('decimalPlaces')}
                options={DECIMAL_PLACES}
                optionContentFullWidth
                defaultValue={(field.decimalPlaces ?? 1).toString()}
              />
              <Form.Select
                id="numberFormat"
                key={`decimalFormat-${allowDecimalNumbers}`}
                label={t('format')}
                options={decimalFormatOptions}
                optionContentFullWidth
                defaultValue={field.numberFormat}
              />
            </>
          ) : (
            <Form.Select
              id="numberFormat"
              key={`numberFormat-${allowDecimalNumbers}`}
              label={t('format')}
              options={numberFormatOptions}
              defaultValue={field.numberFormat}
              optionContentFullWidth
            />
          )}
          {formNumberFormat && (
            <NumberExampleCard
              isDecimal={allowDecimalNumbers}
              fixedDecimals={formDecimalPlaces}
              formatter={formNumberFormat}
            />
          )}
        </>
      );
    }
    return null;
  };

  const getDefaultValueFields = (
    fieldType: DocumentTemplateFieldTypes,
    allowDecimalNumbers: boolean
  ) => {
    if (fieldType === 'select') {
      return (
        <Form.Select
          id="defaultValue"
          label={t('defaultValue')}
          isDisabled={!Boolean(selectedList)}
          options={
            selectedList?.options?.map((option) => ({
              value: option.value,
              label: option.label,
            })) ?? []
          }
          isClearable={false}
          defaultValue={document.defaultData?.[field.id]}
        />
      );
    }
    if (fieldType === 'number') {
      return (
        <Form.NumberField
          id="defaultValue"
          key={`defaultNumber-${allowDecimalNumbers}`}
          label={t('defaultValue')}
          defaultValue={defaultValue}
          placeholder={t('addValuePlaceholder')}
          isDecimal={allowDecimalNumbers}
        />
      );
    }
    if (fieldType === 'date') {
      return (
        <Form.DatePicker
          id="defaultValue"
          label={t('defaultValue')}
          defaultValue={defaultValue}
          placeholder={t('addValuePlaceholder')}
        />
      );
    }
    if (fieldType !== 'file') {
      return (
        <Form.TextField
          id="defaultValue"
          label={t('defaultValue')}
          defaultValue={defaultValue}
          placeholder={t('addValuePlaceholder')}
        />
      );
    }
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={IconDynamicField}
        iconColor="elementBlue100"
        title={t('editVariable')}
      >
        {/* Arrows */}
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt onSubmit={handleSubmit} store={formStore}>
          <Stack space={6}>
            <Container>
              <Text text={t('details')} font="semibold14" />
              <Stack space={4}>
                <FormLabel text={t('variable')} />
                <Text
                  text={`{{${field.id}}}`}
                  font="regular14"
                  style={{ marginBottom: pxToRem(16) }}
                />
                <Form.TextField
                  id="label"
                  label={t('label')}
                  validators={['required']}
                  defaultValue={field.label}
                />
              </Stack>
            </Container>
            <Sidebar.Separator />
            <Container>
              <Text text={t('options')} font="semibold14" />
              <Stack space={6}>
                <Stack space={4}>
                  <Form.Select
                    id="type"
                    label={t('type')}
                    styles={selectDropdownStyles}
                    parseSingleValue={(option) => option.singleValue}
                    options={typeOptions.map(([type, Icon]) => {
                      const label = (
                        <Stack orientation="horizontal" align="center" space={2}>
                          <Icon color="elementPurple50" />
                          <Text text={t(type as DocumentTemplateFieldTypes)} />
                        </Stack>
                      );

                      return {
                        label,
                        value: type,
                        singleValue: label,
                      };
                    })}
                    defaultValue={
                      field.type === 'decimal' || field.type === 'number' ? 'number' : field.type
                    }
                    isClearable={false}
                  />
                  {getExtraFields(fieldType as DocumentTemplateFieldTypes)}
                </Stack>
                <Separator />
                <Stack space={4}>
                  {getDefaultValueFields(
                    fieldType as DocumentTemplateFieldTypes,
                    allowDecimalNumbers
                  )}
                  {fieldType !== 'file' && (
                    <InfoBox
                      icon={<IconFormIndicatorInfo color="indigo100" />}
                      variant="indigo"
                      text={t('useAsDefaultInfo')}
                    />
                  )}
                </Stack>

                <Sidebar.Footer>
                  <Form.Actions>
                    <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
                    <Form.Submit
                      isDisabled={isSubmitDisabled}
                      text={t('core.save')}
                      variant="primary-blue"
                    />
                  </Form.Actions>
                </Sidebar.Footer>
              </Stack>
            </Container>
          </Stack>
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

const selectDropdownStyles: Styles<any, boolean> = {
  singleValue: (provided) => ({
    ...provided,
    display: 'flex',
  }),
  menu: (provided) => ({
    ...provided,
    margin: 0,
    width: '100%',
    borderRadius: theme.space[2],
    boxShadow: theme.shadow.high,
    backgroundColor: theme.color.backgroundLightDark,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: theme.space[4],
    maxHeight: 'auto',
  }),
};

const locale = {
  en: {
    editVariable: 'Edit Variable',
    variable: 'Variable',
    details: 'Details',
    label: 'Label',
    list: 'List',
    useAsDefault: 'Use as default',
    useAsDefaultInfo:
      'Default value is automatically applied when the contract is used, but can always be customized later in the Journey Builder',
    defaultValue: 'Default value',
    type: 'Type',
    text: 'Text short',
    paragraph: 'Text long',
    number: 'Number',
    email: 'Email',
    phone: 'Mobile number',
    date: 'Date',
    select: 'Select',
    file: 'File',
    decimal: 'Decimal',
    addValuePlaceholder: 'Add value...',
    options: 'Options',
    format: 'Format',
    allowDecimalNumbers: 'Allow decimal numbers',
    decimalPlaces: 'Decimal places',
    language: 'Language',
  },
};
